import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const movie = movies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);
  const content = movie || article;

  if (!content) {
    return (
      <>
        <Navbar />
        <div className="pt-20 px-4 text-center min-h-[400px] flex flex-col items-center justify-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
          <h2 className="text-xl text-foreground-700 mb-2">内容不存在</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const isMovie = !!movie;
  const contentArray = isMovie
    ? [content.description, '更多精彩内容请下载黄瓜影院APP观看。']
    : (content as typeof articles[0]).content;

  const articleSchema = isMovie
    ? {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: movie!.title,
        description: movie!.description,
        image: movie!.image,
        dateCreated: `${movie!.year}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: `${movie!.rating}`,
          bestRating: '10',
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article!.title,
        description: article!.summary,
        image: article!.image,
        datePublished: article!.date,
        author: { '@type': 'Person', name: article!.author },
      };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huanggua.tv' },
      {
        '@type': 'ListItem',
        position: 2,
        name: getCategoryName(content.category),
        item: `https://huanggua.tv/category/${content.category}`,
      },
      { '@type': 'ListItem', position: 3, name: content.title },
    ],
  };

  const relatedMovies = isMovie
    ? movies.filter((m) => m.category === movie!.category && m.id !== movie!.id).slice(0, 4)
    : [];

  const relatedArticles = !isMovie && article
    ? article.relatedIds.map((rid) => articles.find((a) => a.id === rid)).filter(Boolean) as typeof articles
    : [];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${content.title} - 黄瓜影院`}
        description={isMovie ? (content as typeof movies[0]).description : (content as typeof articles[0]).summary}
        canonicalUrl={`https://huanggua.tv/article/${id}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: getCategoryName(content.category), to: `/category/${content.category}` },
                { label: content.title },
              ]}
            />

            <header className="mt-6 mb-8">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4">
                {content.title}
              </h1>

              {isMovie && (
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 px-3 py-1 text-sm font-semibold bg-accent-100 text-accent-700 rounded-full">
                    <i className="ri-star-fill"></i> {movie!.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie!.year}</span>
                  <span className="text-sm text-foreground-500">{movie!.duration}</span>
                  {movie!.episodes && (
                    <span className="text-sm text-foreground-500">共{movie!.episodes}集</span>
                  )}
                  {movie!.genre.map((g) => (
                    <span key={g} className="px-2.5 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                      {g}
                    </span>
                  ))}
                </div>
              )}

              {!isMovie && article && (
                <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.author}</span>
                </div>
              )}
            </header>

            {content.image && (
              <div className="rounded-xl overflow-hidden mb-8 aspect-[16/9] max-h-[500px]">
                <img
                  src={content.image}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              {contentArray.map((paragraph, idx) => (
                <p
                  key={idx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Related Content */}
            {(relatedMovies.length > 0 || relatedArticles.length > 0) && (
              <section className="mt-14 pt-10 border-t border-background-200">
                <h3 className="font-heading text-xl text-foreground-900 mb-6">相关推荐</h3>

                {relatedMovies.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                )}

                {relatedArticles.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="group flex gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                            {a.title}
                          </h4>
                          <p className="text-xs text-foreground-500 line-clamp-2">{a.summary}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}